<template>
  <div class="dark-theme-wrapper dynamic-bg">
    <div class="floating-shape shape-1"></div>
    <div class="floating-shape shape-2"></div>

    <div class="container text-center py-5 content-layer">
      <div class="hero-section mb-5 fade-in-up">
        <h1 class="main-title glitch-effect">PRASO TIPOVAČKA</h1>
        <p class="subtitle text-muted mb-4">
          TIPUJ VÝSLEDKY, PORAZ PŘÁTELE, VYHRAJ LIGU.
        </p>

        <div class="d-flex justify-content-center gap-3 flex-wrap">
          <button
            @click="toggleRules"
            class="btn btn-outline-light btn-pill px-4 py-2 hover-lift"
          >
            {{ showRules ? "SKRÝT PRAVIDLA" : "PRAVIDLA" }}
          </button>

          <button
            v-if="!isLoggedIn"
            @click="showLoginModal = true"
            class="btn btn-red-glow pulse-animation px-4"
          >
            PŘIHLÁSIT SE DO HRY
          </button>

          <template v-else>
            <button
              @click="$router.push('/account')"
              class="btn btn-red-glow hover-lift"
            >
              MŮJ ÚČET
            </button>
            <button
              @click="handleLogout"
              class="btn btn-outline-danger hover-lift"
            >
              ODHLÁSIT SE
            </button>
          </template>
        </div>
      </div>

      <transition name="slide-fade">
        <div v-if="showRules" class="rules-container mb-5 glass-panel">
          <GamesRules />
        </div>
      </transition>

      <div v-if="loading" class="mt-5 text-white-50">
        <div class="spinner-border text-danger" role="status"></div>
        <p class="mt-2">Načítám kalendář...</p>
      </div>

      <div v-else class="matches-section">
        <div class="round-header mb-5 fade-in-up" style="animation-delay: 0.2s">
          <h2 class="section-title">
            AKTUÁLNÍ KOLO <span class="round-number">{{ roundNumber }}</span>
          </h2>
          <div class="separator mx-auto"></div>
        </div>

        <div v-if="matches.length" class="row justify-content-center g-4">
          <div
            v-for="(match, index) in matches"
            :key="match.id"
            class="col-md-8 col-lg-6 match-col"
            :style="{ animationDelay: index * 0.1 + 's' }"
          >
            <div class="match-card hover-glow">
              <div class="match-card-content">
                <div class="team-box home">
                  <img
                    :src="match.homeLogo"
                    alt="Home"
                    class="team-logo floating-logo"
                  />
                  <span class="team-name" :title="match.homeTeam">{{
                    match.homeTeam
                  }}</span>
                </div>

                <div class="vs-box">
                  <span class="vs-text">VS</span>
                  <div class="match-time-box">
                    <span class="match-date">{{
                      formatDate(match.matchDate).split(",")[0]
                    }}</span>
                    <span class="match-time">{{
                      formatDate(match.matchDate).split(",")[1]
                    }}</span>
                  </div>
                </div>

                <div class="team-box away">
                  <img
                    :src="match.awayLogo"
                    alt="Away"
                    class="team-logo floating-logo"
                    style="animation-delay: 0.5s"
                  />
                  <span class="team-name" :title="match.awayTeam">{{
                    match.awayTeam
                  }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <p v-else class="mt-5 text-muted fade-in-up">
          Pro toto kolo nejsou k dispozici žádné zápasy.
        </p>
      </div>
    </div>

    <transition name="fade">
      <div
        v-if="showLoginModal"
        class="modal-backdrop"
        @click.self="showLoginModal = false"
      >
        <div class="modal-content glass-panel zoom-in">
          <div class="modal-header d-flex justify-content-end">
            <button class="btn-close-custom" @click="showLoginModal = false">
              <svg
                viewBox="0 0 24 24"
                width="24"
                height="24"
                stroke="currentColor"
                stroke-width="2"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
          <UserLogin />
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import GamesRules from "./GamesRules.vue";
import UserLogin from "./UserLogin.vue";
import { db } from "../firebase"; // <--- ZDE POZOR: Pokud máte auth.js, použijte "../auth"
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  getDoc,
  orderBy,
} from "firebase/firestore";
import { logoutUser } from "../auth";

export default {
  name: "UserHome",
  components: {
    GamesRules,
    UserLogin,
  },
  data() {
    return {
      matches: [],
      roundNumber: null, // Začneme s null
      loading: true,
      showRules: false,
      showLoginModal: false,
      isLoggedIn: !!localStorage.getItem("userRole"),
    };
  },
  async mounted() {
    await this.fetchVisibleRound();
    window.addEventListener("storage", this.checkLoginStatus);
  },
  beforeUnmount() {
    window.removeEventListener("storage", this.checkLoginStatus);
  },
  methods: {
    checkLoginStatus() {
      this.isLoggedIn = !!localStorage.getItem("userRole");
      if (this.isLoggedIn) {
        this.showLoginModal = false;
      }
    },
    toggleRules() {
      this.showRules = !this.showRules;
    },
    async handleLogout() {
      await logoutUser();
      localStorage.removeItem("userRole");
      this.isLoggedIn = false;
    },

    async fetchVisibleRound() {
      try {
        const roundRef = doc(db, "settings", "visibleRound");
        const roundSnap = await getDoc(roundRef);

        if (roundSnap.exists()) {
          // Ujistíme se, že je to číslo
          this.roundNumber = Number(roundSnap.data().roundNumber);
          console.log("Načteno kolo ze settings:", this.roundNumber);
          await this.fetchMatches();
        } else {
          console.warn("Dokument 'settings/visibleRound' neexistuje!");
          this.loading = false;
        }
      } catch (error) {
        console.error("Chyba při načítání kola:", error);
        this.loading = false;
      }
    },

    async fetchMatches() {
      if (this.roundNumber === null) return;

      try {
        const matchesRef = collection(db, "matches");
        // Hledáme podle roundNumber (číslo)
        const q = query(
          matchesRef,
          where("roundNumber", "==", this.roundNumber),
          orderBy("matchDate", "asc") // Seřadíme podle data
        );

        const querySnapshot = await getDocs(q);

        this.matches = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        console.log(
          `Načteno ${this.matches.length} zápasů pro kolo ${this.roundNumber}`
        );
      } catch (error) {
        console.error("Chyba při načítání zápasů:", error);
        // Pokud chybí index pro řazení, Firebase vyhodí chybu s odkazem
        if (error.message.includes("index")) {
          console.warn(
            "Chybí index ve Firestore. Podívejte se do konzole na odkaz pro vytvoření."
          );
        }
      } finally {
        this.loading = false; // Vždy vypneme načítání
      }
    },

    formatDate(date) {
      if (!date) return "";
      // Ošetření pro případ, že je datum Timestamp z Firestore
      const matchDate = date.toDate ? date.toDate() : new Date(date);

      return matchDate.toLocaleString("cs-CZ", {
        day: "numeric",
        month: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
  },
};
</script>

<style scoped>
/* =========================================
   DYNAMIC BACKGROUND & SHAPES
   ========================================= */
.dynamic-bg {
  background: linear-gradient(135deg, #000000 0%, #1a0505 50%, #000000 100%);
  background-size: 200% 200%;
  animation: gradientMove 15s ease infinite;
  min-height: 100vh;
  position: relative;
  overflow-x: hidden;
}

@keyframes gradientMove {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

.floating-shape {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  z-index: 0;
  opacity: 0.4;
  animation: floatShape 10s infinite ease-in-out;
}

.shape-1 {
  width: 300px;
  height: 300px;
  background: #a71d2a;
  top: -50px;
  left: -50px;
  animation-delay: 0s;
}
.shape-2 {
  width: 400px;
  height: 400px;
  background: #5c0b12;
  bottom: -100px;
  right: -100px;
  animation-delay: 5s;
}

@keyframes floatShape {
  0%,
  100% {
    transform: translate(0, 0);
  }
  50% {
    transform: translate(30px, 50px);
  }
}

.content-layer {
  position: relative;
  z-index: 1;
}

/* =========================================
   ELEMENTY & EFEKTY
   ========================================= */
.main-title {
  font-size: 3.5rem;
  font-weight: 900;
  letter-spacing: 3px;
  background: linear-gradient(to right, #fff, #aaa);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 0 0 20px rgba(255, 255, 255, 0.1);
}

.subtitle {
  font-size: 1.1rem;
  letter-spacing: 1px;
}

.round-number {
  color: #dc3545;
  font-size: 2.5rem;
  font-weight: 800;
}

/* =========================================
   KARTA ZÁPASU (REDESIGN)
   ========================================= */
.match-card {
  background: linear-gradient(180deg, #151515 0%, #0d0d0d 100%);
  border: 1px solid #333;
  border-radius: 16px; /* Trochu kulatější rohy */
  padding: 30px 20px;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  position: relative;
  overflow: hidden;
}

.match-card:hover {
  transform: translateY(-5px) scale(1.02);
  border-color: #dc3545;
  box-shadow: 0 15px 30px rgba(220, 53, 69, 0.3);
}

/* Jemná červená záře na pozadí karty při hoveru */
.match-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(
    circle at center,
    rgba(220, 53, 69, 0.2) 0%,
    transparent 70%
  );
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: 0;
}

.match-card:hover::before {
  opacity: 1;
}

.match-card-content {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

/* Týmy */
.team-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 35%; /* Pevná šířka pro týmy */
  text-align: center;
}

.team-logo {
  width: 80px; /* Větší loga */
  height: 80px;
  object-fit: contain;
  filter: drop-shadow(0 0 8px rgba(255, 255, 255, 0.15));
  margin-bottom: 15px;
}

/* Plovoucí efekt loga */
.floating-logo {
  animation: logoFloat 3s ease-in-out infinite;
}
@keyframes logoFloat {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-6px);
  }
}

.team-name {
  font-size: 1.1rem; /* Větší písmo */
  font-weight: 800;
  color: #fff;
  line-height: 1.2;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  /* KLÍČOVÁ ČÁST PRO JEDEN ŘÁDEK */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%; /* Aby ellipsis fungovalo */
  display: block;
}

/* Střední část (VS a Datum) */
.vs-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 20%;
}

.vs-text {
  font-size: 2.2rem;
  font-weight: 900;
  color: #dc3545;
  font-style: italic;
  line-height: 1;
  margin-bottom: 10px;
}

.match-time-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  font-weight: bold;
}

.match-date {
  font-size: 0.9rem;
  color: #ccc;
}

.match-time {
  font-size: 1.1rem;
  color: #fff;
}

/* =========================================
   MODAL & ANIMACE
   ========================================= */
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.85);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(5px);
}

.modal-content {
  width: 90%;
  max-width: 450px;
  padding: 20px;
  position: relative;
  background: #111;
  border: 1px solid #333;
  border-radius: 15px;
}

.btn-close-custom {
  background: none;
  border: none;
  color: #888;
  cursor: pointer;
  transition: color 0.3s;
  padding: 5px;
}
.btn-close-custom:hover {
  color: #dc3545;
}

.pulse-animation {
  animation: pulse-red 2s infinite;
}
@keyframes pulse-red {
  0% {
    box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(220, 53, 69, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(220, 53, 69, 0);
  }
}

.fade-in-up {
  opacity: 0;
  animation: fadeInUp 0.8s ease-out forwards;
}
.match-col {
  opacity: 0;
  animation: fadeInUp 0.6s ease-out forwards;
}
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.4s ease;
}
.slide-fade-enter-from,
.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}
.zoom-in {
  animation: zoomIn 0.3s ease-out;
}
@keyframes zoomIn {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.glass-panel {
  background: rgba(20, 20, 20, 0.8);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
}
.btn-pill {
  border-radius: 50px;
}
.hover-lift:hover {
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(255, 255, 255, 0.1);
}
</style>
